import { FC, SetStateAction, SyntheticEvent, useCallback, useEffect, useState } from 'react'
import { Outlet, useNavigate } from '@tanstack/react-router'
import cn from 'classnames'
import { Seo } from 'components'
import ModalItem from 'components/ModalItem'
import { MODAL_IDS, useModal } from 'components/ModalProvider'
import { isEmpty } from 'helpers/isEmpty'
import { useTypedSelector } from 'hooks/useTypedSelector'
import { useCancelOrderMutation } from 'modules/order/api'
import ModalCancel from 'modules/order/components/ModalCancel/ModalCancel'
import { OrderTable } from 'modules/order/components/OrderTable/OrderTable'
import { TOrder, TPagination } from 'modules/order/types'

import {
  orderCreateRoute,
  orderCreateYarcheRoute,
  orderDetailRoute,
  orderGuestRedirectCreateRoute,
  orderRoute
} from '../../routes'
import { useOrderListData, useOrderListState } from './hooks'

import s from './styles.module.less'
import cs from 'src/common.module.less'

export const OrderList: FC = () => {
  const navigate = useNavigate()
  const { closeModal, openModal } = useModal()
  const [focusedOrder, setFocusedOrder] = useState<TOrder | null>(null)
  const searchParams = orderRoute.useSearch()

  const state = useOrderListState()

  const {
    data,
    isFetching,
    refetch,
    pagination: dataPagination
  } = useOrderListData({
    departure: state.departure,
    destination: state.destination,
    departureDateFrom: state.departureDateFrom,
    departureDateTo: state.departureDateTo,
    status: state.status,
    paymentStatus: state.paymentStatus,
    search: state.search,
    mode: state.mode,
    page: state.pagination.page,
    perPage: state.pagination.perPage
  })

  const pagination = {
    page: state.pagination.page,
    perPage: state.pagination.perPage,
    total: dataPagination.total || 0
  }

  const [cancelOrder, { isLoading: isCanceling }] = useCancelOrderMutation()

  const handleOpenModalDetail = useCallback(
    (order: TOrder) => {
      navigate({
        to: orderDetailRoute.to,
        params: { id: order.id },
        search: (prev) => ({ ...prev, ...searchParams })
      })
    },
    [navigate, searchParams]
  )

  const handleOpenModalCancel = useCallback(
    (e: SyntheticEvent, order: any) => {
      e.stopPropagation()
      setFocusedOrder(order)
      openModal(MODAL_IDS.CancelOrderList)
    },
    [openModal]
  )

  const handleCloseModalCancel = useCallback(() => {
    closeModal(MODAL_IDS.CancelOrderList)
  }, [closeModal])

  const handleOpenOrderCreate = useCallback(() => {
    const isNeedShowGuestDataForm = !isEmpty(sessionStorage.getItem('orderFormData'))

    if (isNeedShowGuestDataForm) {
      navigate(orderGuestRedirectCreateRoute)
    } else {
      navigate(orderCreateRoute)
    }
  }, [navigate])

  const handleOpenOrderCreateYarche = useCallback(() => {
    navigate({ to: orderCreateYarcheRoute.to })
  }, [navigate])

  const orderList = useTypedSelector((state) => state.orderListReducer.isUpdatedOrder)
  useEffect(() => {
    refetch()
  }, [orderList, refetch])

  const handleSetPagination = useCallback(
    (setterAction: SetStateAction<TPagination>) => {
      if (typeof setterAction === 'function') {
        const newPagination = setterAction({
          page: state.pagination.page,
          perPage: state.pagination.perPage,
          total: pagination.total
        })
        state.handleSetPagination({
          page: newPagination.page,
          perPage: newPagination.perPage
        })
      } else {
        state.handleSetPagination({
          page: setterAction.page,
          perPage: setterAction.perPage
        })
      }
    },
    [state, pagination.total]
  )

  return (
    <>
      <Seo
        description="Список размещенных заявок на грузоперевозку в сервисе QP5 Express."
        title="Мои заявки – сборные перевозки QP5 Express"
      />
      <Outlet />
      <div className={cn(cs.container, s.container)}>
        <OrderTable
          departure={state.departure}
          departureDateFrom={state.departureDateFrom}
          departureDateTo={state.departureDateTo}
          destination={state.destination}
          handleOpenModalCancel={handleOpenModalCancel}
          handleOpenModalDetail={handleOpenModalDetail}
          handleOpenOrderCreate={handleOpenOrderCreate}
          handleResetFilter={state.handleResetFilter}
          handleSetDeparture={state.handleSetDeparture}
          handleSetDepartureDateFrom={state.handleSetDepartureDateFrom}
          handleSetDepartureDateTo={state.handleSetDepartureDateTo}
          handleSetDestination={state.handleSetDestination}
          handleSetMode={state.handleSetMode}
          handleSetPaymentStatus={state.handleSetPaymentStatus}
          handleSetSearch={state.handleSetSearch}
          handleSetStatus={state.handleSetStatus}
          handleSwapPoints={state.handleSwapPoints}
          isLoading={isFetching}
          mode={state.mode}
          onOpenOrderCreateYarche={handleOpenOrderCreateYarche}
          orders={data?.data?.order}
          pagination={pagination}
          paymentStatus={state.paymentStatus}
          refetch={refetch}
          search={state.search}
          setPagination={handleSetPagination}
          status={state.status}
        />
      </div>
      <ModalItem id={MODAL_IDS.CancelOrderList}>
        <ModalCancel
          cancelOrder={cancelOrder}
          focusedOrder={focusedOrder}
          id={MODAL_IDS.CancelOrderList}
          isSubmitting={isCanceling}
          onClose={handleCloseModalCancel}
          refetch={refetch}
        />
      </ModalItem>
    </>
  )
}
